<script setup lang="ts">

import UsertypeRadioButton from "@/views/pages/auth/reg/UsertypeRadioButton.vue";
import {computed, getCurrentInstance, onBeforeUnmount, onMounted, reactive, ref, watch} from "vue";
import {WebFormModel} from "capitalist-shared/src/core/model";
import {RemoteRequestState} from "capitalist-shared/src/core/remote";
import ProfileUpdateLegal from "@/views/pages/profile/ProfileUpdateLegal.vue";
import ProfileUpdateIndividual from "@/views/pages/profile/ProfileUpdateIndividual.vue";
import RegularFormNumberedItemHeading
  from "@/views/pages/profile/RegularFormNumberedItemHeading.vue";
import axios from "axios";
import LoadingIndicator from "@/components/loaders/LoadingIndicator.vue";
import {onBeforeRouteLeave} from "vue-router/composables";
import {scrollToElement} from "@/core/scroll";

class UsertypeSelectModel extends WebFormModel {
  loadingState = new RemoteRequestState()
  values = {
    userType: ''
  }

  profile = null
  hasActiveRequest = false
  request = null

  checkState() {
    this.loadingState.loadStart();
    return axios.get('/webapi/profile/update?call=checkState')
      .then((response) => {
        this.loadingState.loadEnd();
        this.profile = response.data.profile;
        this.hasActiveRequest = response.data.hasActiveRequest;
        this.request = response.data.request;
        return response.data;
      })
      .catch((error) => {
        this.checkAxiosError(error);
        this.loadingState.loadError();
        throw error;
      })
  }
}

const model = reactive(new UsertypeSelectModel())

const isIndividual = computed(() => model.values.userType === 'INDIVIDUAL');
const isLegalEntity = computed(() => model.values.userType === 'LEGAL');

const individualForm = ref(null)
const legalForm = ref(null)

watch(() => model.values.userType, (value) => {
  if (value === 'INDIVIDUAL') {
    legalForm.value?.hideForm();
    individualForm.value?.activateForm();
  } else if (value === 'LEGAL') {
    individualForm.value?.hideForm();
    legalForm.value?.activateForm();
  }
})

const doConfirmLeave = ref(true);

const disabled = computed(() => {
  return !model.loadingState.isLoaded
    || !updateAvailable.value
    || model.hasActiveRequest
    || (model.values.userType != '') && (individualForm.value?.busy || legalForm.value?.busy);
})

const selectorRef = ref(null)

const reloadState = () => {
  scrollToElement(selectorRef.value, 500);
  model.values.userType = '';
  return model.checkState();
}

onMounted(() => {
  reloadState()
})

const updateAvailable = computed(() => {
  return model.profile?.updateAvailable;
})

const isConfirmationRequired = () => {
  const result = (model.values.userType !== '') && model.loadingState.isLoaded && updateAvailable.value;
  return result;
}

const confirmationMessage = () => {
  const i18n = getCurrentInstance()?.proxy.$i18n;
  const message = 'Вы действительно хотите покинуть страницу? Несохраненные данные будут потеряны.';
  return i18n ? String(i18n.t(message)) : message;
}

const confirmLeave = () => {
  if (!isConfirmationRequired()) return true;
  return window.confirm(confirmationMessage());
}

onBeforeRouteLeave((to, from, next) => {
  if (confirmLeave()) {
    next();
  } else {
    next(false);
  }
})

const beforeWindowUnload = (e) => {
  if (!isConfirmationRequired()) return true;

  (e || window.event).returnValue = confirmationMessage();

  if (!confirmLeave()) {
    // Cancel the event
    e.preventDefault()
    // Chrome requires returnValue to be set
    e.returnValue = ''
  }
  return confirmationMessage();
}

window.addEventListener('beforeunload', beforeWindowUnload)

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', beforeWindowUnload)
})

</script>

<template>
  <div ref="selectorRef">
    <h1>{{$t('Заявка на верификацию аккаунта')}}</h1>
    <div class="block">
      <div class="block__content">

        <template v-if="model.loadingState.isError">
          <div class="loading--error">
            <p>
              {{ $t('Не удалось загрузить страницу, попробуйте снова.') }}
            </p>
            <div>
              <button type="button" class="btn btn-success"
                      @click="reloadState"
                      :disabled="model.loadingState.isLoading">
                {{ $t('Повторить') }}
              </button>

            </div>
          </div>
        </template>

        <template v-if="!model.loadingState.isError">
          <div class="forms--form-step">
            <RegularFormNumberedItemHeading>
              {{$t('Тип профиля')}}
              <template v-slot:number>
                1
              </template>
            </RegularFormNumberedItemHeading>

            <div class="split-two-columns">
              <UsertypeRadioButton :checked="isIndividual"
                                   :disabled="disabled"
                                   type="individual"
                                   @input="model.values.userType = 'INDIVIDUAL'">{{ $t('Частное лицо') }}
              </UsertypeRadioButton>
              <UsertypeRadioButton :checked="isLegalEntity"
                                   :disabled="disabled"
                                   type="legal-entity"
                                   @input="model.values.userType = 'LEGAL'">{{ $t('Бизнес') }}
              </UsertypeRadioButton>
            </div>
          </div>
        </template>


        <template v-if="model.loadingState.isLoading">
          <LoadingIndicator height="60px" style="margin: 50px auto;" ref="loader"/>
        </template>


        <template v-if="model.loadingState.isLoaded">
          <template v-if="updateAvailable">
            <template v-if="model.hasActiveRequest">
              <div class="has--active--request">
                <h4>
                  {{$t('Ваша заявка на верификацию находится на рассмотрении.')}}
                </h4>
                <a href="/profile" class="btn btn-success" :title="$t('Вернуться на страницу персональных данных') ">
                  {{ $t('Вернуться на страницу персональных данных') }}</a>
              </div>
            </template>
            <template v-else>
              <div v-show="isIndividual" >
                <ProfileUpdateIndividual ref="individualForm"
                                         @submit="reloadState"
                                         @check-profile="reloadState" />
              </div>
              <div v-show="isLegalEntity">
                <ProfileUpdateLegal ref="legalForm"
                                    @submit="reloadState"
                                    @check-profile="reloadState" />
              </div>
            </template>
          </template>
          <template v-else>
            <div class="has--active--request">
              <h4>
                {{$t('Ваш аккаунт уже верифицирован.')}}
              </h4>
              <a href="/profile" class="btn btn-success"
                 :title="$t('Вернуться на страницу персональных данных')">
                {{ $t('Вернуться на страницу персональных данных') }}</a>
            </div>
          </template>
        </template>

      </div>
    </div>

  </div>
</template>

<style scoped lang="less">
.split-two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;
}


.forms--form-step {
  padding: 25px 40px 25px 40px;
}

.loading--error {
  margin: 3rem;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 2rem;
  justify-content: center;

  p {
    padding: 0;
    margin: 0;
  }
}

.has--active--request {
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 2em;
  align-items: center;
  justify-content: center;
  h4 {
    font-size: 2rem;
    font-weight: 900;
    margin: 0; padding: 0;
  }
}
</style>

<i18n>
{
  "en": {
    "Ваш аккаунт уже верифицирован.": "Your account is already verified.",
    "Вы действительно хотите покинуть страницу? Несохраненные данные будут потеряны.": "Are you sure you want to leave the page? Unsaved data will be lost.",
    "Частное лицо": "Individual",
    "Бизнес": "Business",
    "Ваша заявка на верификацию находится на рассмотрении.": "Your account verification application is under review.",
    "Тип профиля": "Profile type",
    "Заявка на верификацию аккаунта": "Account verification application",
    "Вернуться на страницу персональных данных": "Return to personal information page"
  }
}
</i18n>
